import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FinancePageLayout, formatMoney } from '@/components/finance';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Clock3, Download, RefreshCw, Send } from 'lucide-react';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';

type HealthRow = {
  ref_date: string;
  open_issues: number;
  high_critical_open: number;
  over_24h_open: number;
};

type ReconIssue = {
  id: string;
  status: 'open' | 'in_review' | 'resolved' | 'waived';
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: string;
  issue_type: string;
  opened_at: string;
  amount_expected: number | null;
  amount_found: number | null;
  currency: string | null;
};

type PeriodClose = {
  business_unit: 'all' | 'br' | 'us';
  period_year: number;
  period_month: number;
  status: 'open' | 'closed' | 'locked';
  closed_at: string | null;
};

type BankImportBatch = {
  id: string;
  source: 'itau' | 'chase' | 'manual';
  file_name: string;
  parse_status: 'pending' | 'parsed' | 'failed';
  imported_at: string;
};

type WeeklyAuditReport = {
  id: string;
  week_start: string;
  week_end: string;
  generated_at: string;
  summary: Record<string, any>;
};

const ISSUE_TYPE_LABEL: Record<string, string> = {
  missing: 'Sem extrato',
  mismatch: 'Divergência',
  duplicate: 'Duplicada',
  duplicate_suspected: 'Duplicada suspeita',
  orphan: 'Órfã',
  timing: 'Timing',
};

export default function FinanceDashboard() {
  const { data: currentAppUser } = useCurrentAppUser();
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState<HealthRow | null>(null);
  const [recentIssues, setRecentIssues] = useState<ReconIssue[]>([]);
  const [latestClose, setLatestClose] = useState<PeriodClose | null>(null);
  const [lockedPeriods, setLockedPeriods] = useState<PeriodClose[]>([]);
  const [recentImports, setRecentImports] = useState<BankImportBatch[]>([]);
  const [failedWebhookCount, setFailedWebhookCount] = useState(0);
  const [weeklyReport, setWeeklyReport] = useState<WeeklyAuditReport | null>(null);
  const [stripeOpenIssues, setStripeOpenIssues] = useState<ReconIssue[]>([]);
  const [generatingWeekly, setGeneratingWeekly] = useState(false);
  const [downloadingWeeklyCsv, setDownloadingWeeklyCsv] = useState(false);
  const [dispatchingWeeklyPush, setDispatchingWeeklyPush] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [healthRes, issuesRes, closeRes, lockedRes, importsRes, failedWebhookRes, weeklyRes, stripeIssuesRes] = await Promise.all([
        (supabase.from('v_finance_recon_d1_health' as any) as any).select('*').limit(1).maybeSingle(),
        (supabase.from('finance_recon_issues' as any) as any)
          .select('id,status,severity,source,issue_type,opened_at,amount_expected,amount_found,currency')
          .in('status', ['open', 'in_review'])
          .order('opened_at', { ascending: false })
          .limit(6),
        (supabase.from('finance_period_close' as any) as any)
          .select('business_unit,period_year,period_month,status,closed_at')
          .order('period_year', { ascending: false })
          .order('period_month', { ascending: false })
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        (supabase.from('finance_period_close' as any) as any)
          .select('business_unit,period_year,period_month,status,closed_at')
          .eq('status', 'locked')
          .order('period_year', { ascending: false })
          .order('period_month', { ascending: false })
          .limit(6),
        (supabase.from('bank_import_batches' as any) as any)
          .select('id,source,file_name,parse_status,imported_at')
          .order('imported_at', { ascending: false })
          .limit(6),
        (supabase.from('stripe_webhook_events' as any) as any)
          .select('id', { count: 'exact', head: true })
          .eq('process_status', 'failed'),
        (supabase.from('finance_weekly_audit_reports' as any) as any)
          .select('id,week_start,week_end,generated_at,summary')
          .order('week_start', { ascending: false })
          .limit(1)
          .maybeSingle(),
        (supabase.from('finance_recon_issues' as any) as any)
          .select('id,status,severity,source,issue_type,opened_at,amount_expected,amount_found,currency')
          .eq('source', 'stripe')
          .in('status', ['open', 'in_review'])
          .order('opened_at', { ascending: false })
          .limit(5000),
      ]);

      if (healthRes.error) throw healthRes.error;
      if (issuesRes.error) throw issuesRes.error;
      if (closeRes.error) throw closeRes.error;
      if (lockedRes.error) throw lockedRes.error;
      if (importsRes.error) throw importsRes.error;
      if (failedWebhookRes.error) throw failedWebhookRes.error;
      if (weeklyRes.error) throw weeklyRes.error;
      if (stripeIssuesRes.error) throw stripeIssuesRes.error;

      setHealth((healthRes.data || null) as HealthRow | null);
      setRecentIssues(((issuesRes.data || []) as unknown) as ReconIssue[]);
      setLatestClose((closeRes.data || null) as PeriodClose | null);
      setLockedPeriods(((lockedRes.data || []) as unknown) as PeriodClose[]);
      setRecentImports(((importsRes.data || []) as unknown) as BankImportBatch[]);
      setFailedWebhookCount(failedWebhookRes.count || 0);
      setWeeklyReport((weeklyRes.data || null) as WeeklyAuditReport | null);
      setStripeOpenIssues(((stripeIssuesRes.data || []) as unknown) as ReconIssue[]);
    } catch (error: any) {
      console.error('finance cockpit load error:', error);
      toast.error(`Falha ao carregar cockpit financeiro: ${error.message || 'erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const generateWeeklyReport = async () => {
    setGeneratingWeekly(true);
    try {
      const { data, error } = await (supabase.rpc('fn_generate_finance_weekly_audit_report' as any, {
        p_ref_date: new Date().toISOString().slice(0, 10),
        p_generated_by_app_user_id: currentAppUser?.id || null,
      }) as any);
      if (error) throw error;
      toast.success(`Resumo semanal atualizado (${data})`);
      await loadDashboard();
    } catch (error: any) {
      console.error('weekly audit generation error:', error);
      toast.error(`Falha ao gerar resumo semanal: ${error.message || 'erro desconhecido'}`);
    } finally {
      setGeneratingWeekly(false);
    }
  };

  const downloadWeeklyCsv = async () => {
    setDownloadingWeeklyCsv(true);
    try {
      const { data, error } = await supabase.functions.invoke('process-finance-weekly-audit-report', {
        body: { action: 'export_csv' },
      });
      if (error) throw error;

      const csv = data?.csv;
      const filename = data?.filename || `finance-weekly-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      if (!csv || typeof csv !== 'string') {
        throw new Error('CSV inválido retornado pela função.');
      }

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV semanal baixado com sucesso.');
    } catch (error: any) {
      console.error('weekly csv export error:', error);
      toast.error(`Falha ao exportar CSV semanal: ${error.message || 'erro desconhecido'}`);
    } finally {
      setDownloadingWeeklyCsv(false);
    }
  };

  const dispatchWeeklyPush = async () => {
    setDispatchingWeeklyPush(true);
    try {
      const { data, error } = await supabase.functions.invoke('process-finance-weekly-audit-report', {
        body: { action: 'dispatch_push' },
      });
      if (error) throw error;
      toast.success(`Disparo semanal concluído: ${data?.sent || 0} envio(s), ${data?.skipped || 0} pulado(s).`);
    } catch (error: any) {
      console.error('weekly push dispatch error:', error);
      toast.error(`Falha no disparo semanal: ${error.message || 'erro desconhecido'}`);
    } finally {
      setDispatchingWeeklyPush(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const closeStatusBadge = useMemo(() => {
    if (!latestClose) return <Badge variant="secondary">Sem fechamento registrado</Badge>;
    if (latestClose.status === 'closed') return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">Fechado</Badge>;
    if (latestClose.status === 'locked') return <Badge className="bg-slate-100 text-slate-700 border-slate-300">Travado</Badge>;
    return <Badge variant="outline">Aberto</Badge>;
  }, [latestClose]);

  const dailyChecklist = useMemo(() => {
    const hasRecentImport = (recentImports?.length || 0) > 0;
    const openIssues = health?.open_issues || 0;
    const hasCritical = (health?.high_critical_open || 0) > 0;
    const slaOver24h = (health?.over_24h_open || 0) > 0;
    const isPeriodLocked = latestClose?.status === 'locked';
    const stripePending = stripeOpenIssues.length;

    return [
      {
        key: 'imports',
        label: 'Extratos importados',
        ok: hasRecentImport,
        detail: hasRecentImport ? 'Importação recente encontrada.' : 'Sem importação recente.',
        href: '/finance/bank-accounts',
      },
      {
        key: 'open_issues',
        label: 'Fila de conciliação',
        ok: openIssues === 0,
        detail: openIssues === 0 ? 'Sem pendências abertas.' : `${openIssues} pendência(s) aberta(s).`,
        href: '/finance/reconciliation?status=open',
      },
      {
        key: 'sla',
        label: 'SLA de conciliação',
        ok: !hasCritical && !slaOver24h,
        detail: !hasCritical && !slaOver24h
          ? 'Sem críticos e sem itens >24h.'
          : `Críticos: ${health?.high_critical_open || 0} • >24h: ${health?.over_24h_open || 0}`,
        href: '/finance/reconciliation?status=open&sort=sla',
      },
      {
        key: 'period',
        label: 'Período financeiro',
        ok: isPeriodLocked,
        detail: isPeriodLocked ? 'Último período está travado.' : 'Período ainda aberto/fechado sem trava.',
        href: '/finance/close',
      },
      {
        key: 'stripe_open',
        label: 'Stripe não conciliado',
        ok: stripePending === 0,
        detail: stripePending === 0 ? 'Sem pendências Stripe abertas.' : `${stripePending} pendência(s) Stripe aberta(s).`,
        href: '/finance/reconciliation?status=open&source=stripe',
      },
    ];
  }, [recentImports, health, latestClose, stripeOpenIssues.length]);

  const stripeOpenSummary = useMemo(() => {
    const open = stripeOpenIssues.length;
    const orphan = stripeOpenIssues.filter((row) => row.issue_type === 'orphan').length;
    const missing = stripeOpenIssues.filter((row) => row.issue_type === 'missing').length;
    const critical = stripeOpenIssues.filter((row) => row.severity === 'critical' || row.severity === 'high').length;
    const oldestHours = stripeOpenIssues.reduce((max, row) => {
      const age = (Date.now() - new Date(row.opened_at).getTime()) / (1000 * 60 * 60);
      return Number.isFinite(age) ? Math.max(max, age) : max;
    }, 0);
    const currencyTotals = stripeOpenIssues.reduce<Record<string, number>>((acc, row) => {
      const currency = row.currency || 'USD';
      const amount = Math.abs(Number(row.amount_expected ?? row.amount_found ?? 0));
      acc[currency] = (acc[currency] || 0) + amount;
      return acc;
    }, {});
    const primaryCurrency = Object.entries(currencyTotals).sort((a, b) => b[1] - a[1])[0] || ['USD', 0];
    return {
      open,
      orphan,
      missing,
      critical,
      oldestHours,
      primaryCurrency: primaryCurrency[0],
      primaryAmount: primaryCurrency[1],
    };
  }, [stripeOpenIssues]);

  return (
    <FinancePageLayout>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Cockpit Financeiro</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadDashboard} disabled={loading}>
            {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Atualizar
          </Button>
          <Button asChild>
            <Link to="/finance/reconciliation?status=open">Ir para Conciliação</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Checklist Operacional D+1</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {dailyChecklist.map((item) => (
            <div key={item.key} className="flex flex-col gap-2 rounded-md border p-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-2">
                {item.ok ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                )}
                <div>
                  <p className="text-sm font-medium">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.detail}</p>
                </div>
              </div>
              <Button asChild size="sm" variant="outline">
                <Link to={item.href}>Abrir</Link>
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Resumo Semanal de Auditoria</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadWeeklyCsv} disabled={downloadingWeeklyCsv}>
              {downloadingWeeklyCsv ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              CSV
            </Button>
            <Button variant="outline" size="sm" onClick={dispatchWeeklyPush} disabled={dispatchingWeeklyPush}>
              {dispatchingWeeklyPush ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Disparar push
            </Button>
            <Button variant="outline" size="sm" onClick={generateWeeklyReport} disabled={generatingWeekly}>
              {generatingWeekly ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              Gerar agora
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {!weeklyReport ? (
            <p className="text-sm text-muted-foreground">Sem relatório semanal ainda.</p>
          ) : (
            <>
              <p className="text-sm">
                Semana {weeklyReport.week_start} até {weeklyReport.week_end} • gerado em {new Date(weeklyReport.generated_at).toLocaleString()}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="border rounded-md p-2 text-sm">
                  <p className="text-muted-foreground text-xs">Issues abertas agora</p>
                  <p className="font-semibold">{weeklyReport.summary?.issues_open_now ?? 0}</p>
                </div>
                <div className="border rounded-md p-2 text-sm">
                  <p className="text-muted-foreground text-xs">SLA &gt;48h agora</p>
                  <p className="font-semibold text-red-600">{weeklyReport.summary?.issues_open_over48h_now ?? 0}</p>
                </div>
                <div className="border rounded-md p-2 text-sm">
                  <p className="text-muted-foreground text-xs">Mudanças de auditoria (semana)</p>
                  <p className="font-semibold">{weeklyReport.summary?.audit_changes_week ?? 0}</p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Periodos Travados Ativos</CardTitle>
          <Button variant="outline" size="sm" asChild>
            <Link to="/finance/close">Gerenciar no Fechamento</Link>
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {lockedPeriods.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum periodo travado no momento.</p>
          ) : (
            lockedPeriods.map((period) => (
              <div
                key={`${period.business_unit}-${period.period_year}-${period.period_month}`}
                className="border rounded-md p-2 text-sm flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="font-medium">
                    {String(period.period_month).padStart(2, '0')}/{period.period_year} - {period.business_unit.toUpperCase()}
                  </p>
                  {period.closed_at ? (
                    <p className="text-xs text-muted-foreground">Travado em {new Date(period.closed_at).toLocaleString()}</p>
                  ) : null}
                </div>
                <Badge className="bg-slate-100 text-slate-700 border-slate-300">Travado</Badge>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Issues abertas</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-8 w-16" /> : <p className="text-2xl font-bold">{health?.open_issues ?? 0}</p>}
            <Button variant="link" className="px-0 h-auto" asChild>
              <Link to="/finance/reconciliation?status=open">Abrir fila</Link>
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">High/Critical abertas</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-8 w-16" /> : <p className="text-2xl font-bold text-red-600">{health?.high_critical_open ?? 0}</p>}
            <Button variant="link" className="px-0 h-auto" asChild>
              <Link to="/finance/reconciliation?status=open&severity=critical">Ver críticas</Link>
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">&gt;24h abertas</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-8 w-16" /> : <p className="text-2xl font-bold text-orange-600">{health?.over_24h_open ?? 0}</p>}
            <Button variant="link" className="px-0 h-auto" asChild>
              <Link to="/finance/reconciliation?status=open&sla=over24">Atacar SLA</Link>
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Webhooks Stripe com falha</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-8 w-16" /> : <p className="text-2xl font-bold">{failedWebhookCount}</p>}
            <Button variant="link" className="px-0 h-auto" asChild>
              <Link to="/finance/reconciliation?status=open&source=stripe">Ver Stripe</Link>
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Não conciliados Stripe</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <p className="text-2xl font-bold">{stripeOpenSummary.open}</p>
                <p className="text-xs text-muted-foreground">
                  Órfã {stripeOpenSummary.orphan} • Sem extrato {stripeOpenSummary.missing}
                </p>
                <p className="text-xs text-muted-foreground">
                  High/Critical {stripeOpenSummary.critical} • Mais antigo {Math.floor(stripeOpenSummary.oldestHours)}h
                </p>
                <p className="text-xs text-muted-foreground">
                  Exposição: {formatMoney(
                    stripeOpenSummary.primaryAmount,
                    stripeOpenSummary.primaryCurrency === 'BRL' ? 'BRL' : 'USD'
                  )}
                </p>
              </>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              <Button variant="link" className="h-auto p-0 text-xs" asChild>
                <Link to="/finance/reconciliation?status=open&source=stripe">Abrir fila Stripe</Link>
              </Button>
              <Button variant="link" className="h-auto p-0 text-xs" asChild>
                <Link to="/finance/reconciliation?status=open&source=stripe&type=orphan">Só órfã</Link>
              </Button>
              <Button variant="link" className="h-auto p-0 text-xs" asChild>
                <Link to="/finance/reconciliation?status=open&source=stripe&type=missing">Só sem extrato</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Fechamento atual</CardTitle>
            <Button variant="outline" size="sm" asChild>
              <Link to="/finance/close">Abrir fechamento</Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">{closeStatusBadge}</div>
            {latestClose ? (
              <div className="text-sm space-y-1">
                <div>
                  Período: {String(latestClose.period_month).padStart(2, '0')}/{latestClose.period_year}
                </div>
                <div>BU: {latestClose.business_unit.toUpperCase()}</div>
                {latestClose.closed_at ? (
                  <div className="text-muted-foreground">Fechado em {new Date(latestClose.closed_at).toLocaleString()}</div>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhum fechamento encontrado.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Importações bancárias recentes</CardTitle>
            <Button variant="outline" size="sm" asChild>
              <Link to="/finance/bank-accounts">Importar arquivo</Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentImports.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma importação recente.</p>
            ) : (
              recentImports.map((row) => (
                <div key={row.id} className="border rounded-md p-2 text-sm flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{row.file_name}</p>
                    <p className="text-muted-foreground text-xs">
                      {row.source.toUpperCase()} • {new Date(row.imported_at).toLocaleString()}
                    </p>
                  </div>
                  <Badge variant={row.parse_status === 'failed' ? 'destructive' : 'outline'}>{row.parse_status}</Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Issues abertas mais recentes</CardTitle>
          <Button variant="outline" size="sm" asChild>
            <Link to="/finance/reconciliation?status=open">Ver fila completa</Link>
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {recentIssues.length === 0 ? (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Sem issues abertas neste momento.
            </div>
          ) : (
            recentIssues.map((issue) => (
              <div key={issue.id} className="border rounded-md p-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{ISSUE_TYPE_LABEL[issue.issue_type] || issue.issue_type}</p>
                  <p className="text-xs text-muted-foreground">
                    {issue.source} • {new Date(issue.opened_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={issue.severity === 'critical' || issue.severity === 'high' ? 'destructive' : 'outline'}>
                    {issue.severity}
                  </Badge>
                  {health && health.over_24h_open > 0 ? <Clock3 className="h-4 w-4 text-orange-500" /> : null}
                  {issue.severity === 'critical' ? <AlertTriangle className="h-4 w-4 text-red-500" /> : null}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </FinancePageLayout>
  );
}
